<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex overflow-hidden">
    <ClientSidebar />

    <main class="flex-1 flex flex-col h-full overflow-hidden">

      <!-- Header -->
      <header class="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 flex-shrink-0">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Announcements</h2>
          <p class="text-xs text-slate-500">Important updates and messages from your CPA team.</p>
        </div>
        <div v-if="unreadCount > 0" class="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
          <span class="h-2 w-2 rounded-full bg-primary"></span>
          <span class="text-xs font-semibold text-primary">{{ unreadCount }} new</span>
        </div>
      </header>

      <div class="flex-1 overflow-y-auto p-8">

        <!-- Loading -->
        <div v-if="loading" class="flex flex-col items-center justify-center h-full gap-4">
          <svg class="animate-spin h-10 w-10 text-brand-dark" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-slate-400 text-sm">Loading announcements...</p>
        </div>

        <div v-else class="max-w-[800px] mx-auto space-y-4">

          <!-- Empty state -->
          <div v-if="announcements.length === 0" class="bg-white rounded-2xl border border-slate-200 p-16 flex flex-col items-center text-center">
            <span class="material-symbols-outlined text-slate-300 !text-[48px] mb-3">campaign</span>
            <p class="text-slate-700 font-semibold mb-1">No announcements yet</p>
            <p class="text-slate-400 text-sm">Your CPA team will post important updates here.</p>
          </div>

          <!-- Announcement cards — collapsible -->
          <div
            v-for="item in announcements"
            :key="item.Id"
            class="bg-white rounded-xl border transition-all overflow-hidden"
            :class="isNew(item) ? 'border-primary/30 shadow-sm' : 'border-slate-200'">

            <!-- Top bar for new items -->
            <div v-if="isNew(item)" class="h-1 bg-primary"></div>

            <!-- ── Header (always visible) — click to expand ── -->
            <button
              @click="toggleExpand(item.Id)"
              class="w-full flex items-start justify-between gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left">
              <div class="flex flex-col gap-1 min-w-0 flex-1">
                <!-- New badge -->
                <span v-if="isNew(item)"
                  class="self-start px-2 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary">
                  New
                </span>
                <!-- Subject -->
                <h3 class="text-sm font-bold text-slate-900">{{ item.Subject || item.Title }}</h3>
                <span class="text-xs text-slate-400">{{ formatRelativeDate(item.Created) }}</span>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0 mt-1">
                <!-- Attachment indicator -->
                <span v-if="item.AttachmentUrl" class="material-symbols-outlined text-primary" style="font-size: 16px;">attach_file</span>
                <!-- Expand arrow -->
                <span class="material-symbols-outlined text-slate-400 transition-transform"
                  :class="expandedId === item.Id ? 'rotate-180' : ''"
                  style="font-size: 20px;">expand_more</span>
              </div>
            </button>

            <!-- ── Expanded body ── -->
            <div v-if="expandedId === item.Id" class="px-5 pb-5 border-t border-slate-100">
              <!-- Author -->
              <p class="text-xs text-slate-400 mt-3 mb-3 flex items-center gap-1">
                <span class="material-symbols-outlined" style="font-size: 14px;">person</span>
                {{ item.AuthorName || 'Admin' }}
              </p>

              <!-- Message body -->
              <p class="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{{ item.DisplayMessage }}</p>

              <!-- Attachment download -->
              <a v-if="item.AttachmentUrl"
                :href="buildAttachmentUrl(item)"
                target="_blank"
                class="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20 transition-colors">
                <span class="material-symbols-outlined" style="font-size: 18px;">download</span>
                {{ item.AttachmentName || 'Download Attachment' }}
              </a>
            </div>
          </div>

        </div>
      </div>

      <!-- Disclaimer -->
      <p class="px-4 md:px-8 lg:px-12 py-4 text-center leading-relaxed" style="font-size:9px; font-style:italic; color:rgba(0,0,0,0.8);">
        <span class="font-semibold not-italic">Disclaimer:</span> By uploading documents to this portal, you acknowledge that while we employ industry-standard security to protect your data, the transmission of information over the internet is not entirely risk-free. ABC TAX ADVISORY utilizes this third-party platform for your convenience but assumes no responsibility for data security or technical issues arising from its use.
      </p>
    </main>
  </div>
</template>

<script>
import ClientSidebar from '../../components/ClientSidebar.vue';

export default {
  name: 'ClientAnnouncements',
  components: { ClientSidebar },

  data() {
    return {
      loading: true,
      announcements: [],
      seenIds: [],
      expandedId: null
    };
  },

  computed: {
    clientRecord() {
      try {
        const token = sessionStorage.getItem('clientSession');
        if (!token) return {};
        const [data] = token.split('.');
        if (!data) return {};
        const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp || Date.now() > payload.exp) return {};
        return payload;
      } catch { return {}; }
    },

    clientName() {
      return this.clientRecord?.name || '';
    },

    unreadCount() {
      return this.announcements.filter(a => this.isNew(a)).length;
    }
  },

  async mounted() {
    // Load seen announcement IDs from localStorage
    try {
      this.seenIds = JSON.parse(localStorage.getItem('seenAnnouncements') || '[]');
    } catch { this.seenIds = []; }

    await this.loadAnnouncements();

    // Mark all current announcements as seen after viewing
    setTimeout(() => {
      const ids = this.announcements.map(a => a.Id);
      localStorage.setItem('seenAnnouncements', JSON.stringify(ids));
    }, 3000);
  },

  methods: {
    buildAttachmentUrl(item) {
      const token = sessionStorage.getItem('clientSession') || '';
      if (item.DriveItemId && item.DriveId) {
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(item.DriveId)}&itemId=${encodeURIComponent(item.DriveItemId)}&token=${encodeURIComponent(token)}`;
      }
      if (item.AttachmentUrl) {
        return `/.netlify/functions/get-file?path=${encodeURIComponent(item.AttachmentUrl)}&token=${encodeURIComponent(token)}`;
      }
      return '#';
    },

    async loadAnnouncements() {
      this.loading = true;
      try {
        this.announcements = await this.$sharepoint.getClientAnnouncements(this.clientName);
      } catch (error) {
        console.error('Error loading announcements:', error);
      } finally {
        this.loading = false;
      }
    },

    isNew(item) {
      return !this.seenIds.includes(item.Id);
    },

    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },

    formatRelativeDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
};
</script>